import ExcelJS from 'exceljs';
import {
  bordeFino,
  fill,
  fuente,
  FMT_CANTIDAD,
  FMT_MONEDA,
  FMT_PORCENTAJE,
  XLSX_COLOR,
  XLSX_FILL,
} from '@/shared/lib/xlsxStyle';
import { COLUMNAS, col, type DatosValorizado } from './exportValorizado';

/**
 * "Inventario valorizado" — calcado del archivo que armó el cliente el
 * 2026-09-04 y mandó como formato a replicar.
 *
 * Dos hojas:
 *  - **Resumen**: tarjetas + composición por estado + composición por tipo +
 *    cómo leer el reporte.
 *  - **Inventario**: el detalle por artículo, con encabezado agrupado, fila
 *    de TOTAL arriba, filtros y paneles congelados.
 *
 * Lo importante no es la pintura: **los totales son fórmulas**, no números
 * pegados. La fila TOTAL usa `SUBTOTAL(109,…)`, que ignora lo que el filtro
 * esconde, así que filtrar por Hornos recalcula todo; y las tarjetas del
 * Resumen apuntan a las celdas de la otra hoja. Un export con totales
 * estáticos miente apenas alguien toca un filtro.
 */

/** Fila donde arrancan los datos en la hoja Inventario. */
const FILA_DATOS = 7;
const FILA_TOTAL = 6;
const FILA_SUBTITULOS = 5;
const FILA_GRUPOS = 4;

export type ContextoValorizado = {
  /** 'Argentina' / 'México'. */
  pais: string;
  /** 'ARS' / 'MXN'. */
  moneda: string;
  /** 'pesos argentinos' / 'pesos mexicanos'. */
  monedaNombre: string;
  /** `dd/mm/yyyy`. */
  fecha: string;
  /** True cuando lo exportado es la vista filtrada y no todo el inventario. */
  vistaFiltrada: boolean;
};

/** `1 → A`, `27 → AA`. ExcelJS no expone un helper público para esto. */
function letra(n: number): string {
  let s = '';
  let x = n;
  while (x > 0) {
    const r = (x - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
}

/** Rango absoluto de una columna sobre las filas de datos. */
function rangoDatos(key: Parameters<typeof col>[0], filas: number): string {
  const c = letra(col(key));
  return `Inventario!$${c}$${FILA_DATOS}:$${c}$${FILA_DATOS + filas - 1}`;
}

export function buildInventarioWorkbook(
  datos: DatosValorizado,
  ctx: ContextoValorizado,
): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Fornitalia';
  wb.created = new Date();
  // El Resumen va primero: es lo que se mira, el detalle es el respaldo.
  const resumen = wb.addWorksheet('Resumen');
  const detalle = wb.addWorksheet('Inventario');
  armarDetalle(detalle, datos, ctx);
  armarResumen(resumen, datos, ctx);
  return wb;
}

// ---------------------------------------------------------------------------
// Hoja Inventario
// ---------------------------------------------------------------------------

function armarDetalle(
  ws: ExcelJS.Worksheet,
  datos: DatosValorizado,
  ctx: ContextoValorizado,
): void {
  const nFilas = datos.filas.length;
  const ultimaFila = FILA_DATOS + Math.max(nFilas, 1) - 1;
  const ultimaCol = COLUMNAS.length;

  ws.columns = COLUMNAS.map((c) => ({
    width:
      c.key === 'articulo' ? 44 : c.key === 'modelo' ? 18 : c.key === 'total_c' ? 10 : c.key === 'total_v' || c.key === 'sinVender_v' ? 16 : c.kind === 'cantidad' ? 9 : 15,
  }));

  const titulo = ws.getCell('A1');
  titulo.value = 'Inventario valorizado — detalle por artículo';
  titulo.font = fuente({ size: 16, bold: true });
  ws.getRow(1).height = 24;

  const sub = ws.getCell('A2');
  sub.value =
    `${ctx.pais} · ${ctx.moneda} · al ${ctx.fecha} · ${nFilas} artículo${nFilas === 1 ? '' : 's'}` +
    (ctx.vistaFiltrada ? ' · vista filtrada' : '');
  sub.font = fuente({ size: 10, color: XLSX_COLOR.gris });
  ws.getRow(2).height = 15.75;
  ws.getRow(3).height = 6;

  // --- Fila 4: encabezado agrupado (un merge por grupo) ---
  const filaGrupos = ws.getRow(FILA_GRUPOS);
  filaGrupos.height = 19.5;
  let inicio = 1;
  for (let i = 1; i <= ultimaCol; i++) {
    const actual = COLUMNAS[i - 1].grupo;
    const siguiente = i < ultimaCol ? COLUMNAS[i].grupo : null;
    const celda = filaGrupos.getCell(i);
    celda.value = actual;
    celda.font = fuente({ size: 9, bold: true, color: XLSX_COLOR.blanco });
    celda.fill = fill(XLSX_FILL.oscuro);
    celda.alignment = { horizontal: 'center', vertical: 'middle' };
    if (i === inicio && inicio > 1) celda.border = { left: bordeFino };
    if (actual !== siguiente) {
      if (i > inicio) ws.mergeCells(FILA_GRUPOS, inicio, FILA_GRUPOS, i);
      inicio = i + 1;
    }
  }

  // --- Fila 5: subtítulos (Tipo / Modelo / … / Cant. / Valor) ---
  const filaSub = ws.getRow(FILA_SUBTITULOS);
  filaSub.height = 21.75;
  COLUMNAS.forEach((c, i) => {
    const celda = filaSub.getCell(i + 1);
    celda.value = c.titulo;
    celda.font = fuente({ size: 9, bold: true });
    celda.fill = fill(XLSX_FILL.encabezado);
    celda.alignment = {
      horizontal: c.kind === 'texto' ? 'left' : 'right',
      vertical: 'middle',
    };
    celda.border = {
      bottom: bordeFino,
      ...(c.titulo === 'Cant.' ? { left: bordeFino } : {}),
    };
  });

  // --- Fila 6: TOTAL con SUBTOTAL(109) — sigue al filtro ---
  const filaTotal = ws.getRow(FILA_TOTAL);
  filaTotal.height = 21.75;
  ws.mergeCells(FILA_TOTAL, 1, FILA_TOTAL, 3);
  const rotulo = filaTotal.getCell(1);
  rotulo.value = 'TOTAL  ·  se recalcula al filtrar';
  rotulo.font = fuente({ size: 10, bold: true });
  rotulo.alignment = { horizontal: 'right', vertical: 'middle' };
  COLUMNAS.forEach((c, i) => {
    const celda = filaTotal.getCell(i + 1);
    celda.fill = fill(XLSX_FILL.total);
    celda.border = {
      top: bordeFino,
      bottom: bordeFino,
      ...(c.titulo === 'Cant.' ? { left: bordeFino } : {}),
      ...(c.key === 'sinVender_v' ? { left: bordeFino } : {}),
    };
    if (i >= 3) {
      celda.font = fuente({ size: 10, bold: true });
      celda.alignment = { horizontal: 'right', vertical: 'middle' };
    }
    // El precio de venta NO se totaliza: sumar precios unitarios de
    // artículos distintos no significa nada.
    if (c.key === 'precio' || c.kind === 'texto') return;
    const L = letra(i + 1);
    celda.value = {
      formula: `SUBTOTAL(109,${L}${FILA_DATOS}:${L}${ultimaFila})`,
      date1904: false,
    };
    celda.numFmt = c.kind === 'moneda' ? FMT_MONEDA : FMT_CANTIDAD;
  });

  // --- Datos ---
  datos.filas.forEach((fila, idx) => {
    const row = ws.getRow(FILA_DATOS + idx);
    row.height = 15.75;
    const esZebra = idx % 2 === 1;
    COLUMNAS.forEach((c, i) => {
      const celda = row.getCell(i + 1);
      const v = fila[i];
      // Vacío = celda VACÍA, no cadena vacía: un "" cuenta como no-blanco en
      // COUNTBLANK y ensucia la tarjeta de artículos sin precio.
      celda.value = v === null || v === undefined || v === '' ? null : v;
      celda.font = fuente({ size: 10 });
      celda.alignment = {
        horizontal: c.kind === 'texto' ? 'left' : 'right',
        vertical: 'middle',
      };
      celda.border = { bottom: bordeFino };
      if (c.kind === 'moneda') celda.numFmt = FMT_MONEDA;
      if (c.kind === 'cantidad') celda.numFmt = FMT_CANTIDAD;
      // Las columnas de cierre (Stock total y Sin vender) van siempre
      // resaltadas; el resto alterna zebra.
      const destacada =
        c.key === 'total_c' || c.key === 'total_v' || c.key === 'sinVender_v';
      if (destacada) celda.fill = fill(XLSX_FILL.destacado);
      else if (esZebra) celda.fill = fill(XLSX_FILL.zebra);
    });
  });

  ws.autoFilter = {
    from: { row: FILA_SUBTITULOS, column: 1 },
    to: { row: ultimaFila, column: ultimaCol },
  };
  // Congela los datos del producto y todo el encabezado: al scrollear a la
  // derecha se sigue viendo de qué artículo es cada número.
  ws.views = [{ state: 'frozen', xSplit: 3, ySplit: FILA_TOTAL, topLeftCell: 'D7' }];

  if (nFilas > 0) {
    const S = letra(col('sinVender_v'));
    ws.addConditionalFormatting({
      ref: `${S}${FILA_DATOS}:${S}${ultimaFila}`,
      rules: [
        {
          type: 'dataBar',
          priority: 1,
          cfvo: [{ type: 'num', value: 0 }, { type: 'max' }],
          color: { argb: 'FF9DB4D0' },
        } as unknown as Parameters<typeof ws.addConditionalFormatting>[0]['rules'][number],
      ],
    });
  }
}

// ---------------------------------------------------------------------------
// Hoja Resumen
// ---------------------------------------------------------------------------

/** Las tarjetas de arriba: rótulo, fórmula, pie y color del número. */
function tarjetas(datos: DatosValorizado) {
  const n = datos.filas.length;
  const r = (key: Parameters<typeof col>[0]) => rangoDatos(key, Math.max(n, 1));
  return [
    {
      label: 'STOCK TOTAL VALORIZADO',
      formula: `SUM(${r('total_v')})`,
      pie: `${n} artículo${n === 1 ? '' : 's'} · todos los estados`,
      color: XLSX_COLOR.texto,
      fmt: FMT_MONEDA,
    },
    {
      label: 'SIN VENDER',
      formula: `SUM(${r('sinVender_v')})`,
      pie: 'Disp. + préstamo + por ingresar',
      color: XLSX_COLOR.azul,
      fmt: FMT_MONEDA,
    },
    {
      label: 'DISPONIBLE',
      formula: `SUM(${r('disponible_v')})`,
      pie: 'Listo para venta inmediata',
      color: XLSX_COLOR.texto,
      fmt: FMT_MONEDA,
    },
    {
      label: 'POR INGRESAR',
      formula: `SUM(${r('porIngresar_v')})`,
      pie: 'Aún no ingresó a depósito',
      color: XLSX_COLOR.texto,
      fmt: FMT_MONEDA,
    },
    {
      label: 'ARTÍCULOS SIN PRECIO',
      // Sin precio = celda vacía; el 0 explícito también cuenta por si
      // alguien lo carga a mano en la planilla.
      formula: `COUNTIF(${r('precio')},0)+COUNTBLANK(${r('precio')})`,
      pie: 'Se valorizan en $0',
      color: XLSX_COLOR.ambar,
      fmt: FMT_CANTIDAD,
    },
  ];
}

/** Filas de "Composición del stock por estado". */
const ESTADOS = [
  { nombre: 'Disponible', cant: 'disponible_c', valor: 'disponible_v', sinVender: true },
  { nombre: 'Préstamo / SR', cant: 'prestShow_c', valor: 'prestShow_v', sinVender: true },
  { nombre: 'Por ingresar', cant: 'porIngresar_c', valor: 'porIngresar_v', sinVender: true },
  { nombre: 'Pago pendiente', cant: 'reservado_c', valor: 'reservado_v', sinVender: false },
  { nombre: 'Pago parcial', cant: 'parcial_c', valor: 'parcial_v', sinVender: false },
  {
    nombre: 'Pendiente de despacho',
    cant: 'despacho_c',
    valor: 'despacho_v',
    sinVender: false,
  },
] as const;

function armarResumen(
  ws: ExcelJS.Worksheet,
  datos: DatosValorizado,
  ctx: ContextoValorizado,
): void {
  const n = Math.max(datos.filas.length, 1);
  const r = (key: Parameters<typeof col>[0]) => rangoDatos(key, n);
  // Columnas angostas entre medio: hacen de separador entre tarjetas sin
  // depender de merges.
  ws.columns = [
    { width: 2.5 },
    { width: 27 },
    { width: 2 },
    { width: 22 },
    { width: 2 },
    { width: 22 },
    { width: 2 },
    { width: 22 },
    { width: 2 },
    { width: 22 },
    { width: 2.5 },
  ];

  const titulo = ws.getCell('B2');
  titulo.value = 'Inventario valorizado';
  titulo.font = fuente({ size: 20, bold: true });
  ws.getRow(2).height = 27.75;

  const sub = ws.getCell('B3');
  sub.value =
    `${ctx.pais} · ${ctx.monedaNombre} (${ctx.moneda}) · al ${ctx.fecha}` +
    (ctx.vistaFiltrada ? ' · vista filtrada' : '');
  sub.font = fuente({ size: 10, color: XLSX_COLOR.gris });
  ws.getRow(3).height = 15.75;

  // --- Tarjetas (filas 5-7, una cada dos columnas desde B) ---
  ws.getRow(5).height = 13.5;
  ws.getRow(6).height = 24;
  ws.getRow(7).height = 13.5;
  tarjetas(datos).forEach((t, i) => {
    const c = 2 + i * 2;
    const rotulo = ws.getRow(5).getCell(c);
    rotulo.value = t.label;
    rotulo.font = fuente({ size: 8, bold: true, color: XLSX_COLOR.gris });
    rotulo.fill = fill(XLSX_FILL.suave);
    rotulo.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    rotulo.border = { left: bordeFino, right: bordeFino, top: bordeFino };

    const valor = ws.getRow(6).getCell(c);
    valor.value = { formula: t.formula, date1904: false };
    valor.numFmt = t.fmt;
    valor.font = fuente({ size: 13, bold: true, color: t.color });
    valor.fill = fill(XLSX_FILL.suave);
    valor.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    valor.border = { left: bordeFino, right: bordeFino };

    const pie = ws.getRow(7).getCell(c);
    pie.value = t.pie;
    pie.font = fuente({ size: 8, color: XLSX_COLOR.gris });
    pie.fill = fill(XLSX_FILL.suave);
    pie.alignment = { horizontal: 'left', vertical: 'top', indent: 1 };
    pie.border = { left: bordeFino, right: bordeFino, bottom: bordeFino };
  });

  // --- Composición por estado ---
  seccion(ws, 10, 'Composición del stock por estado');
  encabezadoTabla(ws, 11, ['Estado', 'Unidades', 'Valorizado', '% del total', 'Integra "Sin vender"']);
  const filaTotalEstados = 12 + ESTADOS.length;
  ESTADOS.forEach((e, i) => {
    const fila = 12 + i;
    ws.getRow(fila).height = 18;
    celdaTabla(ws, fila, 2, e.nombre, { align: 'left' });
    celdaTabla(ws, fila, 4, { formula: `SUM(${r(e.cant)})`, date1904: false }, { fmt: FMT_CANTIDAD });
    celdaTabla(ws, fila, 6, { formula: `SUM(${r(e.valor)})`, date1904: false }, { fmt: FMT_MONEDA });
    celdaTabla(
      ws,
      fila,
      8,
      { formula: `IFERROR(F${fila}/$F$${filaTotalEstados},0)`, date1904: false },
      { fmt: FMT_PORCENTAJE },
    );
    celdaTabla(ws, fila, 10, e.sinVender ? 'Sí' : '—', { align: 'right' });
  });
  ws.getRow(filaTotalEstados).height = 19.5;
  filaTotalTabla(ws, filaTotalEstados, [
    { c: 2, v: 'Stock total', align: 'left' },
    { c: 4, v: { formula: `SUM(D12:D${filaTotalEstados - 1})`, date1904: false }, fmt: FMT_CANTIDAD },
    { c: 6, v: { formula: `SUM(F12:F${filaTotalEstados - 1})`, date1904: false }, fmt: FMT_MONEDA },
    { c: 8, v: 1, fmt: FMT_PORCENTAJE },
    { c: 10, v: null },
  ]);

  // --- Composición por tipo ---
  const filaSeccionTipos = filaTotalEstados + 2;
  seccion(ws, filaSeccionTipos, 'Composición por tipo de artículo');
  const filaEncabezadoTipos = filaSeccionTipos + 1;
  encabezadoTabla(ws, filaEncabezadoTipos, [
    'Tipo',
    'Artículos',
    'Unidades',
    'Valorizado',
    '% del total',
  ]);
  const primeraTipo = filaEncabezadoTipos + 1;
  const filaTotalTipos = primeraTipo + Math.max(datos.tipos.length, 0);
  datos.tipos.forEach((tipo, i) => {
    const fila = primeraTipo + i;
    ws.getRow(fila).height = 18;
    celdaTabla(ws, fila, 2, tipo, { align: 'left' });
    celdaTabla(
      ws,
      fila,
      4,
      { formula: `COUNTIF(${r('tipo')},$B${fila})`, date1904: false },
      { fmt: FMT_CANTIDAD },
    );
    celdaTabla(
      ws,
      fila,
      6,
      { formula: `SUMIF(${r('tipo')},$B${fila},${r('total_c')})`, date1904: false },
      { fmt: FMT_CANTIDAD },
    );
    celdaTabla(
      ws,
      fila,
      8,
      { formula: `SUMIF(${r('tipo')},$B${fila},${r('total_v')})`, date1904: false },
      { fmt: FMT_MONEDA },
    );
    celdaTabla(
      ws,
      fila,
      10,
      { formula: `IFERROR(H${fila}/$H$${filaTotalTipos},0)`, date1904: false },
      { fmt: FMT_PORCENTAJE },
    );
  });
  ws.getRow(filaTotalTipos).height = 19.5;
  const rangoTipos = `${primeraTipo}:${Math.max(filaTotalTipos - 1, primeraTipo)}`;
  const [desdeTipo, hastaTipo] = rangoTipos.split(':');
  filaTotalTabla(ws, filaTotalTipos, [
    { c: 2, v: 'Total general', align: 'left' },
    { c: 4, v: { formula: `SUM(D${desdeTipo}:D${hastaTipo})`, date1904: false }, fmt: FMT_CANTIDAD },
    { c: 6, v: { formula: `SUM(F${desdeTipo}:F${hastaTipo})`, date1904: false }, fmt: FMT_CANTIDAD },
    { c: 8, v: { formula: `SUM(H${desdeTipo}:H${hastaTipo})`, date1904: false }, fmt: FMT_MONEDA },
    { c: 10, v: 1, fmt: FMT_PORCENTAJE },
  ]);

  // --- Cómo leer este reporte ---
  const filaNotas = filaTotalTipos + 2;
  const notas = ws.getCell(filaNotas, 2);
  notas.value = 'Cómo leer este reporte';
  notas.font = fuente({ size: 10, bold: true });
  ws.getRow(filaNotas).height = 15;
  [
    `· Valores en ${ctx.monedaNombre} (${ctx.moneda}), calculados como cantidad × precio de venta vigente.`,
    '· "Sin vender" = disponible + préstamo/SR + por ingresar. No incluye lo ya comprometido (pago pendiente, pago parcial y pendiente de despacho).',
    '· Los artículos sin precio cargado se valorizan en $0; revisar esa lista antes de tomar decisiones sobre el total.',
    '· En la hoja Inventario, la fila TOTAL se recalcula automáticamente al usar los filtros del encabezado.',
    '· Los importes se muestran redondeados; el dato subyacente conserva los decimales.',
  ].forEach((texto, i) => {
    const fila = filaNotas + 1 + i;
    ws.getRow(fila).height = 13.5;
    const celda = ws.getCell(fila, 2);
    celda.value = texto;
    celda.font = fuente({ size: 9, color: XLSX_COLOR.gris });
  });
}

function seccion(ws: ExcelJS.Worksheet, fila: number, texto: string): void {
  const celda = ws.getCell(fila, 2);
  celda.value = texto;
  celda.font = fuente({ size: 12, bold: true });
  ws.getRow(fila).height = 18;
}

function encabezadoTabla(ws: ExcelJS.Worksheet, fila: number, titulos: string[]): void {
  ws.getRow(fila).height = 19.5;
  titulos.forEach((t, i) => {
    const celda = ws.getCell(fila, 2 + i * 2);
    celda.value = t;
    celda.font = fuente({ size: 9, bold: true });
    celda.fill = fill(XLSX_FILL.encabezado);
    celda.alignment = { horizontal: i === 0 ? 'left' : 'right', vertical: 'middle' };
    celda.border = { bottom: bordeFino };
  });
}

type ValorCelda = string | number | null | ExcelJS.CellFormulaValue;

function celdaTabla(
  ws: ExcelJS.Worksheet,
  fila: number,
  columna: number,
  valor: ValorCelda,
  opts: { fmt?: string; align?: 'left' | 'right' } = {},
): void {
  const celda = ws.getCell(fila, columna);
  celda.value = valor as ExcelJS.CellValue;
  celda.font = fuente({ size: 10 });
  celda.alignment = { horizontal: opts.align ?? 'right', vertical: 'middle' };
  celda.border = { bottom: bordeFino };
  if (opts.fmt) celda.numFmt = opts.fmt;
}

function filaTotalTabla(
  ws: ExcelJS.Worksheet,
  fila: number,
  celdas: Array<{ c: number; v: ValorCelda; fmt?: string; align?: 'left' | 'right' }>,
): void {
  for (const { c, v, fmt, align } of celdas) {
    const celda = ws.getCell(fila, c);
    celda.value = v as ExcelJS.CellValue;
    celda.font = fuente({ size: 10, bold: true });
    celda.fill = fill(XLSX_FILL.total);
    celda.alignment = { horizontal: align ?? 'right', vertical: 'middle' };
    celda.border = { top: bordeFino, bottom: bordeFino };
    if (fmt) celda.numFmt = fmt;
  }
}
